import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import type { PatchThingToDoItemViewModel } from './models'
import {
  extractEditableStartDateTimeStrings,
  updateThingToDoPayloadFromViewModel,
} from './utils'
import CustomThingToDoFields from '../Common/CustomThingToDoFields'
import { formOptions } from '../Common/Fields/config'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { ThingToDoItem } from 'tripPlanner/types/tripItem'

const itemToView = (item: ThingToDoItem): PatchThingToDoItemViewModel => {
  const { startDate, startTime } = extractEditableStartDateTimeStrings(item)
  return {
    type: item.type,
    name: item.name,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    startDate,
    startTime,
    // End-place input removed from thing to do.
    // Setting the end-place to be the same as the start-place.
    // Same deal for endDate and endTime...
    endDate: startDate,
    endTime: startTime,
    startPlace: item.startPlace,
    endPlace: item.startPlace,
    isEndDateSame: true,
    isEndPlaceSame: true,
  }
}

interface Props {
  item: ThingToDoItem
}

function EditThingToDo(props: Props) {
  const { item } = props

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchThingToDoItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateAttractionItem = useCallback(
    ({ ...viewModel }: PatchThingToDoItemViewModel) => {
      const data = updateThingToDoPayloadFromViewModel(viewModel)

      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: data,
      })
    },
    [item.id, item.tripId, updateMutate],
  )

  const onSubmit = useMemo(
    () => handleSubmit(updateAttractionItem),
    [handleSubmit, updateAttractionItem],
  )

  const trashIcon = useMemo(() => <LineTrashAltIcon />, [])

  return (
    <>
      <ModalHeader title="Edit thing to do" onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <CustomThingToDoFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save',
          disabled: isLoading,
          onClick: onSubmit,
        }}
        secondaryActionProps={{
          children: 'Delete',
          disabled: isLoading,
          onClick: deleteItem,
          startIcon: trashIcon,
        }}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditThingToDo
